import { HOME_PAGE, LOGIN_REDIRECT_PAGE } from '@/common/constants/pages';
import { addPrefixToHost } from '@/common/helpers/addPrefixToHost';
import { isSameSite } from '@/common/helpers/isSameSite';
import { navigateTo } from '@/common/helpers/navigateTo';
import Notifier from '@/common/services/Notifier';
import Storage, {
  KEY_AUTH_REDIRECT_STARTED,
  KEY_AUTH_TOKEN,
  KEY_URI_AFTER_AUTH,
  KEY_WAS_LOGGED_OUT,
} from '@/common/services/Storage';
import config from '@/config';
import { NETBOX_ORIGIN } from '@/netbox/constants/network';

export default class AuthService {
  static clearAuthData() {
    Storage.STORAGE_LOCAL.clear();
  }

  static redirectToLoginPage() {
    // Avoid a situation when this handler executes many times (on each 401 request) in a short period of time,
    // it creates many redirects and each redirect creates a session on backend,
    // when the user fills an auth form, he will see CSRF token mismatch error and the only way to pass auth is to reload the page
    if (Storage.hasObject(KEY_AUTH_REDIRECT_STARTED, Storage.STORAGE_SESSION)) {
      return;
    }

    Storage.saveObject(KEY_AUTH_REDIRECT_STARTED, true, Storage.STORAGE_SESSION);

    // do not save "LOGIN_REDIRECT_PAGE" path in store, because it might lead to circle
    if (window.location.pathname !== LOGIN_REDIRECT_PAGE) {
      Storage.saveObject(KEY_URI_AFTER_AUTH, window.location.href, Storage.STORAGE_SESSION);
      Storage.removeObject(KEY_AUTH_TOKEN, Storage.STORAGE_LOCAL);
    }

    navigateTo(AuthService.generateAuthUrl());
  }

  // logout and redirects to main listing page
  static logout() {
    AuthService.clearAuthData();

    navigateTo(`${window.location.origin}${HOME_PAGE}`);
  }

  // removes invalid token and redirects to backend login page
  static logoutAfterSessionExpiration() {
    Storage.saveObject(KEY_WAS_LOGGED_OUT, true, Storage.STORAGE_SESSION);
    AuthService.clearAuthData();
  }

  static getAuthHost() {
    return window.location.origin.includes(NETBOX_ORIGIN) ? window.location.origin : addPrefixToHost('auth');
  }

  // auth in iframe available only within same domain
  static canAuthInFrame() {
    return isSameSite(window.location.origin, AuthService.getAuthHost());
  }

  // generates auth server url
  static generateAuthUrl() {
    const origin = window.location.origin;
    const clientId = origin.includes(NETBOX_ORIGIN) ? config.NETBOX_CLIENT_ID : config.CLIENT_ID;

    return `${AuthService.getAuthHost()}/api/oauth/v2/auth?client_id=${clientId}&grant_type=token&redirect_uri=${origin}/auth&response_type=token`;
  }

  // generates logout server url
  static generateLogoutUrl() {
    return `${AuthService.getAuthHost()}/logout`;
  }

  // this function should be called after refreshing of page
  static showNoticeAboutSessionExpirationIfNeeded() {
    const wasLoggedOut = Storage.getObject(KEY_WAS_LOGGED_OUT, Storage.STORAGE_SESSION);

    if (wasLoggedOut) {
      Storage.removeObject(KEY_WAS_LOGGED_OUT, Storage.STORAGE_SESSION);
      Notifier.warn('You were logged out due to session expiration.', { delay: 10, toastId: null });
    }
  }
}
